import { ScheduleDateType } from "@/components/scheduledDateDropdown/scheduledDateDropdown";
import { DateTime } from "luxon";
import { canScheduleOrder } from "./dateTimeUtils";
import { daysOfWeek } from "./dateTimeUtils";
import { DayPart } from "@/dto/dayPartMenu";
import { Merchant } from "@/dto/merchant";
import { Service } from "@/dto/service";
import { isWithinBusinessHours } from "./restaurantCartUtils";
import { isEmpty, isNil } from "lodash";
import { isBusinessOpen } from "./isBusinessOpenUtil";

export const getSchedulableDateOptions = (
  scheduleDateRange: number,
  businessHours: any,
  timezone: string,
  leadTime?: number,
  daypartMenu?: DayPart | null
): ScheduleDateType[] => {
  const today = DateTime.now().setZone(timezone);

  const dateOptions: ScheduleDateType[] = [];
  for (let i = 0; i <= scheduleDateRange; i++) {
    const date = today.plus({ days: i });
    const dayName = daysOfWeek[date.weekday % 7];
    const formattedDate = date.toFormat("MMM d");
    let businessHoursForDay = getBusinessHoursOfDay(
      businessHours,
      dayName,
      daypartMenu
    );

    if (businessHoursForDay?.length > 0) {
      const nowInMerchantZone = DateTime.now().setZone(timezone);
      if (
        canScheduleOrder(
          businessHoursForDay,
          nowInMerchantZone,
          timezone,
          leadTime,
          i === 0
        )
      ) {
        if (i === 0) {
          dateOptions.push({
            displayValue: "Today",
            dateObject: date.toJSDate(),
          });
        } else if (i === 1) {
          dateOptions.push({
            displayValue: `Tomorrow (${formattedDate})`,
            dateObject: date.toJSDate(),
          });
        } else {
          dateOptions.push({
            displayValue: formattedDate,
            dateObject: date.toJSDate(),
          });
        }
      }
    }
  }

  return dateOptions;
};

export const getBusinessHoursOfDay = (
  businessHours: any,
  dayName: string,
  daypartMenu?: DayPart | null
) => {
  const businessHoursForDay = businessHours?.[dayName] || [];
  if (!daypartMenu) return businessHoursForDay;

  const { days: dpmDays, time: dpmTime } = daypartMenu || {};

  if (!dpmDays?.map((d) => d.toLowerCase()).includes(dayName) || !dpmTime)
    return [];

  const { startTime: start, endTime: end } = dpmTime;
  const menuHours = businessHoursForDay.map((slot: any) => {
    const startTime = Math.max(slot.start, start);
    const endTime = Math.min(slot.end, end);
    return { start: startTime, end: endTime };
  });

  return menuHours
    .filter((slot: any) => slot.start < slot.end)
    .map((slot: any) => ({
      start: slot.start.toString(),
      end: slot.end.toString(),
    }));
};

export const canAcceptASAPOrders = (
  merchant: Merchant,
  isActiveDayPartMenu: boolean
) => {
  if (!isActiveDayPartMenu) return false;

  const now = new Date();
  const currentDateTime = DateTime.fromJSDate(now, {
    zone: merchant.timezone,
  });
  const merchantService = merchant.services.find(
    (s: Service) => s.type === "PICKUP" || s.type === "WEBSITE"
  );
  const businessHours = merchantService?.hours;

  const isNowWithinBusinessHours = isWithinBusinessHours(
    currentDateTime,
    businessHours,
    merchant?.timezone,
    merchant.leadTime
  );

  return isNowWithinBusinessHours;
};

export const isOrderSchedulingEnabled = (merchant: Merchant) => {
  if (!merchant || isEmpty(merchant.scheduledOrdering)) return false;

  if (!merchant.scheduledOrdering.enabled) return false;

  if (
    merchant.scheduledOrdering.maxDays === 0 ||
    isNil(merchant.scheduledOrdering.maxDays)
  )
    return false;

  return true;
};

export const canMerchantAcceptOrders = (
  merchant: Merchant,
  isDineIn: boolean,
  dayPartMenu: DayPart | null
) => {
  const isActiveDayPartMenu = dayPartMenu?.activeNow ?? true;
  //isBusinessOpen checks if busines is accepting online orders
  const isOpen = isBusinessOpen(merchant, isDineIn, isActiveDayPartMenu);

  //If not restaurant merchante, return if business open or not
  if (!merchant.isRestaurant) return isOpen;

  //If business is not open return false
  if (!isOpen) return false;

  //check if scheduling order is enabled for merchant
  const isSchedulingEnabled = isOrderSchedulingEnabled(merchant);

  //check if ASAP orders can be placed for current menu based on hours and current time
  const acceptingASAPOrders = canAcceptASAPOrders(
    merchant,
    isActiveDayPartMenu
  );
  //If orderscheduling is disabled, then return if asap orders accepted or not
  if (!isSchedulingEnabled) {
    return acceptingASAPOrders;
  }

  //If order scheduling is enabled then get schedulable dates. Either ASAP orders should be acceptable or should be able to schedule order based on menu availability and business hours
  const merchantService = merchant?.services.find(
    (s: Service) => s.type === "PICKUP" || s.type === "WEBSITE"
  );
  const schedulableDateOptions = getSchedulableDateOptions(
    merchant?.scheduledOrdering.maxDays - 1,
    merchantService?.hours,
    merchant.timezone,
    merchant.leadTime,
    dayPartMenu
  );

  return acceptingASAPOrders || schedulableDateOptions?.length > 0;
};
